/**
 * Zdalnie or Stacjonarnie — two radio rows, each carrying the sentence that says what it costs.
 *
 * The mode is the one choice on this form with a consequence the trainer cannot read off the label:
 * Stationary books THEIR diary and unlocks the hours field. So the sentence is wired as the radio's
 * description rather than left as loose text beside it.
 *
 * Elastic is not here. `AssignmentScheduleInputValidation.cs:66-70` refuses it outright, so a third
 * row could only ever produce a 400 — `ASSIGN_EXECUTION_MODES` is the list, and it has two members.
 * The three tokens are the backend's own enum members and are never localised; only their labels.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import choice from '~/shared/ui/choiceRow.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import type { AssignExecutionMode } from '../model/assignRequest';

import { HomeGlyph, PhoneGlyph } from './AssignGlyphs';
import styles from './AssignPlanDialog.module.css';

/** Wire token → the lower-case key its copy lives under. Explicit, never `toLowerCase()`. */
const COPY_KEY: Readonly<Record<AssignExecutionMode, string>> = {
  Stationary: 'stationary',
  Remote: 'remote',
  Elastic: 'elastic',
};

interface ModeRowsProps {
  readonly modes: readonly AssignExecutionMode[];
  readonly mode: AssignExecutionMode;
  readonly onChange: (mode: AssignExecutionMode) => void;
  readonly locked: boolean;
  readonly reasonId: string | null;
}

export function ModeRows({ modes, mode, onChange, locked, reasonId }: ModeRowsProps) {
  const { t } = useTranslation();
  const groupName = useId();
  const descriptionPrefix = useId();

  return (
    <div className={choice.rows} role="radiogroup" aria-label={t('web:assign.mode.legend')}>
      {modes.map((option) => {
        const key = COPY_KEY[option];
        const selected = option === mode;
        const descriptionId = `${descriptionPrefix}-${key}`;
        return (
          <label
            key={option}
            className={`${String(choice.row)} ${selected ? String(choice.rowSelected) : ''}`}
          >
            <input
              className={hiddenControl.control}
              type="radio"
              name={groupName}
              value={option}
              checked={selected}
              // Not `disabled`: the control keeps its place in the tab order so the reason attached
              // to it stays reachable. The handler is what refuses.
              aria-disabled={locked ? 'true' : undefined}
              aria-describedby={[descriptionId, reasonId].filter(Boolean).join(' ')}
              onChange={() => {
                if (!locked) onChange(option);
              }}
            />
            <span className={choice.rowMark} aria-hidden="true" />
            <span
              className={`${String(styles.rowGlyph)} ${selected ? String(styles.rowGlyphOn) : ''}`}
              aria-hidden="true"
            >
              {option === 'Stationary' ? <HomeGlyph /> : <PhoneGlyph />}
            </span>
            <span className={choice.rowText}>
              <span className={choice.rowTitle}>{t(`web:assign.mode.${key}.title`)}</span>
              <span id={descriptionId} className={choice.rowMeta}>
                {t(`web:assign.mode.${key}.desc`)}
              </span>
            </span>
          </label>
        );
      })}
    </div>
  );
}
